import React, { useMemo, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import { Server } from "@wajeez/wajeez.microservices";
import { Input } from "@ui/input";
import { changeEmail, checkEmail, useAppDispatch } from "@lib/stores";
import { AuthActions } from "@lib/stores/auth/slices";
import { emailRegex } from "@utils/helpers/emailValidation";
import NewEmailForm from "./NewEmailForm";
import TranslatedText from "@components/items/display/TranslatedText";
import { translate } from "@modules/localizations/translate";

const ChangeEmailRow = ({ user }: { user: Server.AuthenticateOutput }) => {
  const dispatch = useAppDispatch();
  const [editState, setEditState] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<{ email: string }>({
    defaultValues: { email: user.name || "" },
    mode: "all",
  });

  useEffect(() => {
    setValue("email", user.email || "");
    reset({ email: user.email || "" });
  }, [user.email, setValue, reset]);

  const buttonText = useMemo(() => {
    if (user.email) {
      return editState ? "save" : "edit";
    }
    return editState ? "save" : "enter your email";
  }, [user.email, editState]);

  const onSubmit = async (data: { email: string }) => {
    try {
      const { email } = data;
      if (isDirty) {
        checkEmail(email, (isExist) => {
          const body = {
            oldEmail: user?.email ?? "",
            newEmail: email,
          };
          changeEmail(body, (res) => {
            dispatch(AuthActions.getMyInfoAsync());
          });
        });
      }
      setEditState(false);
      reset();
    } catch (error) {
      console.error("Error updating username:", error);
    }
  };

  return (
    <div className="grid w-full grid-cols-3 items-center py-5">
      <TranslatedText
        text={`settings.titles.${"email"}`}
        className="text-black dark:text-white"
      />

      {user.email ? (
        editState ? (
          <div className="col-span-2 grid grid-cols-2 items-center gap-2">
            <Controller
              name="email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: translate(`settings.errors.${"required email"}`),
                },
                pattern: {
                  value: emailRegex(),
                  message: translate(`settings.errors.${"invalid email"}`),
                },
              }}
              render={({ field }) => (
                <div className="flex w-full flex-col">
                  <Input
                    {...field}
                    className={`focus-visible:ring-0 focus-visible:ring-offset-0 ${
                      errors?.email?.message && "border-red-500"
                    }`}
                  />
                  <p className="mt-2 self-start text-[11px] text-red-500">
                    {errors?.email?.message}
                  </p>
                </div>
              )}
            />
            <ShadcnButton
              className="bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent"
              action={handleSubmit(onSubmit)}
            >
              <TranslatedText
                text={`settings.titles.${buttonText}`}
                className="cursor-pointer text-[15px] font-medium text-primary hover:opacity-70 dark:text-white"
              />
            </ShadcnButton>
          </div>
        ) : (
          <div className="col-span-2 grid grid-cols-2 items-center gap-2">
            <div className="dark:text-white">{user.email}</div>
            <ShadcnButton
              className="bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent"
              action={() => setEditState(true)}
            >
              <TranslatedText
                text={`settings.titles.${buttonText}`}
                className="w-full cursor-pointer text-[15px] font-medium text-primary hover:opacity-70 ltr:text-right rtl:text-left dark:text-white"
              />
            </ShadcnButton>
          </div>
        )
      ) : (
        <NewEmailForm />
      )}
    </div>
  );
};

export default ChangeEmailRow;
